import React, { useState } from 'react';
import { useAuth, authApi } from '../lib/auth';
import { OperaWindow, OperaSection, OperaField } from './OperaWindow';

interface Props {
  forced?: boolean;       // true = first-login change, cannot be dismissed
  onDone?: () => void;    // optional callback after successful change
  onCancel?: () => void;  // optional cancel (only valid if !forced)
}

export function ChangePasswordModal({ forced = false, onDone, onCancel }: Props) {
  const { refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must differ from current password.');
      return;
    }
    setBusy(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      await refresh();           // refetch /me, mustChangePassword now false
      onDone?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <OperaWindow
      title="CHANGE PASSWORD"
      status={forced ? 'REQUIRED' : ''}
      onClose={forced ? () => { /* blocked */ } : (onCancel ?? (() => {}))}
      size="compact"
      sideButtons={[
        { label: 'Change', onClick: handleSubmit, primary: true, disabled: busy },
        ...(forced ? [] : [{ label: 'Cancel', onClick: onCancel ?? (() => {}), disabled: busy }]),
      ]}
    >
      {error && <div className="modal-error">{error}</div>}

      {forced && (
        <div style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 8 }}>
          You must change your password before you can use the system.
        </div>
      )}

      <OperaSection title="Password Change">
        <OperaField label="Current">
          <input
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
            disabled={busy}
          />
        </OperaField>
        <OperaField label="New">
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
          />
        </OperaField>
        <OperaField label="Confirm">
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
          />
        </OperaField>
      </OperaSection>

      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
        Minimum 8 characters. All other sessions will be signed out.
      </div>
    </OperaWindow>
  );
}
